import {
  isValidToken,
  refreshAccessToken,
  revokeRefreshToken,
  setSession,
  userClient,
} from "../utils/jwt";
import { gql } from "@apollo/client";
import { usePersistedReducer } from "hooks/usePersistedReducer";
import React, { createContext, useCallback, useEffect } from "react";

const INITIALIZE = "INITIALIZE";
const SIGN_IN = "SIGN_IN";
const SIGN_OUT = "SIGN_OUT";

const ME_QUERY = gql`
  query MeQuery {
    userMe {
      avatarLarge
      avatarMedium
      avatarSmall
      email
      firstName
      lastName
      verified
      isSuperuser
    }
  }
`;

const LOGIN_MUTATION = gql`
  mutation LoginMutation(
    $email: String!
    $password: String!
    $turnstileToken: String
  ) {
    tokenAuth(
      email: $email
      password: $password
      turnstileToken: $turnstileToken
    ) {
      success
      errors
      token {
        token
      }
      user {
        avatarLarge
        avatarMedium
        avatarSmall
        id
        email
        firstName
        lastName
        isSuperuser
        verified
      }
    }
  }
`;

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  isSuperuser: false,
  user: null,
};

const JWTReducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE:
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        isSuperuser: action.payload.isSuperuser,
        user: action.payload.user,
      };
    case "SIGN_IN":
      return {
        ...state,
        isAuthenticated: true,
        isSuperuser: action.payload.isSuperuser,
        user: action.payload.user,
      };
    case "SIGN_OUT":
      return {
        ...state,
        isAuthenticated: false,
        isSuperuser: false,
        user: null,
      };

    default:
      return state;
  }
};

const AuthContext = createContext<any>(null);

function AuthProvider({ children }) {
  const { state, dispatch } = usePersistedReducer(
    JWTReducer,
    initialState,
    "auth"
  );

  const reloadUser = useCallback(
    async (shouldDispatch: () => boolean = () => true) => {
      const data = await userClient.query({
        query: ME_QUERY,
        fetchPolicy: "network-only",
      });

      const user = data.data.userMe;
      if (shouldDispatch()) {
        dispatch({
          type: INITIALIZE,
          payload: {
            isAuthenticated: user != null,
            isSuperuser: user?.isSuperuser ?? false,
            user,
          },
        });
      }

      return user;
    },
    [dispatch]
  );

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      try {
        let accessToken = window.localStorage.getItem("accessToken");

        if (!accessToken || !isValidToken(accessToken)) {
          accessToken = await refreshAccessToken({
            redirectOnFailure: false,
          }).catch(() => null);
        }

        if (!isMounted) {
          return;
        }

        if (accessToken) {
          setSession(accessToken);

          if (!isMounted) {
            return;
          }

          await reloadUser(() => isMounted);
        } else {
          setSession(null);
          dispatch({
            type: INITIALIZE,
            payload: {
              isAuthenticated: false,
              isSuperuser: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        if (!isMounted) {
          return;
        }
        setSession(null);
        dispatch({
          type: INITIALIZE,
          payload: {
            isAuthenticated: false,
            isSuperuser: false,
            user: null,
          },
        });
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, [reloadUser]);

  const signIn = async (email, password, turnstileToken = null) => {
    const data = await userClient.mutate({
      mutation: LOGIN_MUTATION,
      variables: {
        email: email,
        password: password,
        turnstileToken: turnstileToken,
      },
    });

    const tokenAuth = data.data.tokenAuth;
    if (!tokenAuth.success) {
      const errors = tokenAuth.errors;
      throw new Error(
        errors?.nonFieldErrors?.[0]?.message ||
          errors?.turnstileToken?.[0]?.message ||
          "Unable to sign in"
      );
    }
    setSession(tokenAuth.token.token);
    const user = tokenAuth.user;

    dispatch({
      type: SIGN_IN,
      payload: {
        isSuperuser: user.isSuperuser,
        user,
      },
    });
  };

  const signOut = async () => {
    await revokeRefreshToken();
    dispatch({ type: SIGN_OUT });
  };

  const resetPassword = (email) => console.log(email);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "jwt",
        signIn,
        signOut,
        resetPassword,
        reloadUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
